import routes from '../../../constants/routes';
import { FormulaSequenceStatus } from '../../../constants/sequence';

import envConfig from '../../../services/env-config';

import { BreadcrumbItem } from '../Breadcrumb';

const validExactPrefixes = new Set<string>();
const dynamicPathBases = new Set<string>();

const collectPrefixes = (path: string) => {
    const segs = path.split('/').filter(Boolean);
    for (let i = 0; i < segs.length; i++) {
        validExactPrefixes.add('/' + segs.slice(0, i + 1).join('/'));
    }
};

Object.values(routes).forEach((r) => {
    if (typeof r === 'string') {
        collectPrefixes(r);
    } else if (typeof r === 'function') {
        try {
            const MARK = '\x01';
            const result = r(...Array(r.length || 1).fill(MARK));
            if (typeof result !== 'string') return;
            const pathOnly = result.split('?')[0];
            const markIdx = pathOnly.indexOf(MARK);
            if (markIdx <= 0) return;
            const base = pathOnly.slice(0, markIdx).replace(/\/$/, '');
            if (base) {
                collectPrefixes(base);
                dynamicPathBases.add(base);
            }
        } catch {
            // Skip routes that error with placeholder arguments
        }
    }
});

const isKnownPath = (path: string): boolean => {
    if (validExactPrefixes.has(path)) return true;
    const lastSlash = path.lastIndexOf('/');
    if (lastSlash > 0) {
        const parent = path.slice(0, lastSlash);
        return dynamicPathBases.has(parent);
    }
    return false;
};

interface DropdownOption {
    label: string;
    value: string;
}
const dropdownRoutesMap: Record<string, DropdownOption[]> = {
    sequences: [
        {
            label: 'Live',
            value: routes.messageSequencesCatalogWithTabs(
                FormulaSequenceStatus.Live,
            ),
        },
        {
            label: 'Draft',
            value: routes.messageSequencesCatalogWithTabs(
                FormulaSequenceStatus.Draft,
            ),
        },
        {
            label: 'Archived',
            value: routes.messageSequencesCatalogWithTabs(
                FormulaSequenceStatus.Archived,
            ),
        },
    ],
    monitoring: [
        { label: 'Analytics', value: routes.optimizedMonitoringMain },
        { label: 'Controlled', value: routes.controlledMonitoringMain },
        { label: 'Insights', value: routes.insightsMonitoringMain },
        { label: 'Label management', value: routes.labelManagement },
        { label: 'Message performance', value: routes.liveMessages },
        { label: 'Surface performance', value: routes.surfaceMonitoring },
    ],
    'message settings': [
        { label: 'Audiences', value: routes.audience },
        { label: 'Datasets', value: routes.datasetCatalog },
        { label: 'Email templates', value: routes.emailTemplatesCatalog },
        { label: 'Triggers', value: routes.compoundTriggers },
    ],
};

const UUID_REGEX =
    /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export const generateBreadcrumbs = (pathname: string): BreadcrumbItem[] => {
    // Strip base path from pathname if it exists
    const basePath = envConfig.BASE_PATH || '/';
    const pathWithoutBase =
        basePath !== '/' && pathname.startsWith(basePath)
            ? pathname.slice(basePath.length)
            : pathname;

    const segments = pathWithoutBase
        .split('/')
        .filter((segment) => segment !== '');
    const breadcrumbs: BreadcrumbItem[] = [];

    let containsDropdown = false;
    // Iterate over segments and add breadcrumbs until we add a dropdown
    for (let index = 0; index < segments.length; index++) {
        const segment = segments[index];
        const label =
            segment.charAt(0).toUpperCase() +
            segment.slice(1).replace('-', ' ');
        const value = '/' + segments.slice(0, index + 1).join('/');

        if (label.toLowerCase() in dropdownRoutesMap) {
            // Add the dropdown breadcrumb and break out of the loop
            const dropdownOptions = dropdownRoutesMap[label.toLowerCase()];
            breadcrumbs.push({
                label: `${label.replace('-', ' ')}`,
                value,
                dropdownOptions,
            });
            containsDropdown = true;
            break;
        } else if (!isKnownPath(value)) {
            // Stop at segments that don't correspond to a known route
            break;
        } else if (UUID_REGEX.test(segment)) {
            // UUID segments are internal identifiers — stop here so the caller's
            // currentBreadcrumb can provide the human-readable entity name
            break;
        } else {
            breadcrumbs.push({ label, value });
        }
    }

    // remove any extraneous parts of the url (like formulaIds)
    // examples:
    // messages/Draft/2637 will return /messages/Draft but then on the page that shows the message details the currentBreadcrumb prop should provide the name of the message that corresponds to 2637
    // system-setup/attributes/2/values?name=country&page_size=10
    // here, the 2 is removed and the name passed in currentBreadcrumb
    // in some instances, there are further segments but they currently always correspond to inner page tabs, which we are not supposed to be tracking with breadcrumbs.
    // this may need to be adjusted eventually as it is somewhat brittle,  but for now all routes seem to follow this pattern and it avoids trying to parse or understand whether a segment is human readable or is a key that we have to parse.

    return !containsDropdown ? breadcrumbs.slice(0, 2) : breadcrumbs;
};

/**
 * Returns the closest valid base path if the current pathname's second segment
 * is not a known route (indicating a user-entered invalid slug like
 * /system-setup/typo). Returns null if no redirect is needed.
 * Only targets the second path segment to avoid redirecting away from valid
 * detail pages whose IDs appear at the third segment or deeper.
 */
export const getInvalidSegmentRedirect = (pathname: string): string | null => {
    const basePath = envConfig.BASE_PATH || '/';
    const pathWithoutBase =
        basePath !== '/' && pathname.startsWith(basePath)
            ? pathname.slice(basePath.length)
            : pathname;

    const segments = pathWithoutBase.split('/').filter(Boolean);
    if (segments.length < 2) return null;

    const firstValue = '/' + segments[0];
    if (!isKnownPath(firstValue)) return null;

    const secondValue = '/' + segments.slice(0, 2).join('/');
    if (isKnownPath(secondValue)) return null;

    return firstValue;
};

export const generateBreadcrumbSelectedOption = (pathname: string): any => {
    // Strip base path from pathname if it exists
    const basePath = envConfig.BASE_PATH || '/';
    const pathWithoutBase =
        basePath !== '/' && pathname.startsWith(basePath)
            ? pathname.slice(basePath.length)
            : pathname;

    const segments = pathWithoutBase
        .split('/')
        .filter((segment) => segment !== '');

    let label =
        segments[1].charAt(0).toUpperCase() +
            segments[1].slice(1).replace('-', ' ') || '';

    const value = pathname;
    return { label: label, value: value };
};
